import React from 'react';

import { SmartAssistChat } from './SmartAssistChat';
import { SmartAssistRecentPanel } from './SmartAssistRecentPanel';
import { SmartAssistSuggestions } from './SmartAssistSuggestions';
import { useSmartAssist } from './useSmartAssist';

import './_smartAssist.scss';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import clsx from 'clsx';
import i18n from 'i18n-react';

const DEFAULT_BASE_URL = 'https://travpro-ai.on-forge.com/api/v1';

export interface SmartAssistProps {
  firstName?: string;
  destination?: string;
  appId?: number | string | null;
  baseUrl?: string;
  getToken: () => Promise<string | null>;
}

export function SmartAssist({
  firstName,
  destination,
  appId,
  baseUrl = DEFAULT_BASE_URL,
  getToken,
}: SmartAssistProps): React.ReactElement {
  const {
    query, setQuery,
    focused, setFocused,
    chatOpen,
    messages,
    historyLoading,
    messageQuota,
    trainingProgress,
    recentPanelRect,
    inputRef, chatInputRef, wrapperRef, recentPanelRef, lastMessageRowRef,
    greeting,
    destinationLabel,
    sidebarRows,
    recentForPortal,
    ragBarItems,
    isStreaming,
    isQuotaBlocked,
    persistedThread,
    activeSidebarThreadId,
    quickSuggestionsHome,
    quickSuggestionsChat,
    showHomeSuggestionSwitch,
    showChatSuggestionSwitch,
    deletingConvId,
    openChat,
    openSavedConversation,
    openPastConversations,
    closeChat,
    handleNewChat,
    handleSubmit,
    handleChatSubmit,
    handleSuggestionClick,
    handleHeadingClick,
    handleHeadingKeyDown,
    rotateHomeSuggestions,
    rotateChatSuggestions,
    handleDeleteConversation,
  } = useSmartAssist({ firstName, destination, appId, baseUrl, getToken });

  return (
    <>
      <div className="smartAssist" ref={wrapperRef}>
        <div className="smartAssist__corner-actions">
          <button
            type="button"
            className="smartAssist__past-conversations"
            onClick={openPastConversations}
            aria-label={i18n.translate('smartAssist.pastConversations') as string}
            title={i18n.translate('smartAssist.pastConversations') as string}
          >
            <FontAwesomeIcon icon={['fas', 'clock-rotate-left']} />
            <span>{i18n.translate('smartAssist.pastConversations')}</span>
          </button>
          {showHomeSuggestionSwitch && (
            <button
              type="button"
              className={clsx(
                'smartAssist__quick-suggestions-switch',
                'smartAssist__quick-suggestions-switch--corner',
                'smartAssist__quick-suggestions-switch--next',
              )}
              onClick={rotateHomeSuggestions}
              aria-label={i18n.translate('smartAssist.showNextSuggestionsAria') as string}
            >
              <FontAwesomeIcon icon={['fas', 'exchange-alt']} />
            </button>
          )}
        </div>

        <div
          className={clsx(
            'smartAssist__heading',
            persistedThread?.conversationId && 'smartAssist__heading--resumable',
          )}
          role="button"
          tabIndex={0}
          onClick={handleHeadingClick}
          onKeyDown={handleHeadingKeyDown}
          aria-label={
            persistedThread?.conversationId
              ? (i18n.translate('smartAssist.headingResumeAria') as string)
              : (i18n.translate('smartAssist.headingAria') as string)
          }
        >
          <FontAwesomeIcon icon={['fas', 'sparkles']} className="smartAssist__heading-icon" />
          <span>{i18n.translate('smartAssist.title')}</span>
        </div>

        <div className={clsx('smartAssist__widget', focused && 'smartAssist__widget--focused')}>
          <form onSubmit={handleSubmit} className="smartAssist__form">
            <input
              ref={inputRef}
              type="text"
              className="smartAssist__input"
              placeholder={i18n.translate('smartAssist.homePlaceholder', { destinationLabel }) as string}
              value={query}
              onChange={(e) => {
                setQuery(e.target.value);
              }}
              onFocus={() => {
                setFocused(true);
              }}
            />
            <button type="submit" className="smartAssist__submit">
              <FontAwesomeIcon icon={['fas', 'arrow-right']} />
            </button>
          </form>
        </div>

        <SmartAssistSuggestions suggestions={quickSuggestionsHome} onSelect={openChat} />
      </div>

      <SmartAssistRecentPanel
        focused={focused}
        recentForPortal={recentForPortal}
        recentPanelRect={recentPanelRect}
        recentPanelRef={recentPanelRef}
        onOpen={openSavedConversation}
      />

      <SmartAssistChat
        chatOpen={chatOpen}
        greeting={greeting}
        destinationLabel={destinationLabel}
        messages={messages}
        historyLoading={historyLoading}
        messageQuota={messageQuota}
        trainingProgress={trainingProgress}
        isStreaming={isStreaming}
        isQuotaBlocked={isQuotaBlocked}
        query={query}
        sidebarRows={sidebarRows}
        activeSidebarThreadId={activeSidebarThreadId}
        ragBarItems={ragBarItems}
        quickSuggestionsChat={quickSuggestionsChat}
        showChatSuggestionSwitch={showChatSuggestionSwitch}
        lastMessageRowRef={lastMessageRowRef}
        chatInputRef={chatInputRef}
        onClose={closeChat}
        onNewChat={handleNewChat}
        onChatSubmit={handleChatSubmit}
        onSuggestionClick={handleSuggestionClick}
        onSavedConversationOpen={openSavedConversation}
        onDeleteConversation={handleDeleteConversation}
        deletingConvId={deletingConvId}
        onRotateChatSuggestions={rotateChatSuggestions}
        onQueryChange={(e) => {
          setQuery(e.target.value);
        }}
      />
    </>
  );
}
